import './App.scss';

import React, { Suspense, useEffect, useState } from 'react';
import { Avatar, Layout, Menu, Dropdown, Spin, Button } from 'antd';
import { Switch, Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  UserOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  LogoutOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  TeamOutlined,
  RocketOutlined,
  GiftOutlined,
} from '@ant-design/icons';

import logoImage from './static/logo.png';
import logoColor from './static/logo-color.png';
import * as Auth from './services/auth';
import { IState } from './redux/reducers';

import { Organization } from './models/organization';
import PrivateRoute from 'components/PrivateRoute';
import { Scope } from 'redux/reducers/authReducer';
import { usePermission } from 'hooks/usePermission';
import { useCurrentOrganizations, useCurrentUser, useScreenSize } from 'hooks';
import { OrganizationInvitationsModal } from 'components/organism/organizations/organization-invitations-modal';
import { FreeTrialBanner } from 'pages/subscription/free-trial-banner';
import { PastDueBanner } from 'pages/subscription/past-due-banner';
import { OrganizationDropdown } from 'components/organism/organizations/organizations-dropdown';
import { AppMenu } from './Menu';
import { ReferModal } from 'components/organism/subscriptions/refer-modal';
import { ModalContextProvider, useModalContext } from 'components/organism/crud/modal-context';
import { PlanOffer } from './pages/subscription/plan-offer';
import { ToggleFrontendVersion } from 'services/auth.service';

function ReferButton() {
  const isSmallScreen = useScreenSize();
  const modalContext = useModalContext();

  const onClick = () => {
    modalContext.setVisible(true);
  };

  if (isSmallScreen) {
    return <Button shape="circle" type="text" icon={<GiftOutlined />} onClick={onClick}></Button>;
  }

  return (
    <Button shape="round" icon={<GiftOutlined />} onClick={onClick}>
      Refer & Earn
    </Button>
  );
}

const Setup = React.lazy(() => import('./pages/setup'));
const Agreements = React.lazy(() => import('./pages/agreements'));
const Profile = React.lazy(() => import('./pages/new-profile'));
const OrganizationSettings = React.lazy(() => import('./pages/organization/settings'));
const OrganizationStaff = React.lazy(() => import('./pages/organization/staff'));
const Contacts = React.lazy(() => import('./pages/contacts.page'));
const Shifts = React.lazy(() => import('./pages/shifts'));
const Invoices = React.lazy(() => import('./pages/invoices'));
const Rates = React.lazy(() => import('./pages/rates.page'));
const Users = React.lazy(() => import('./pages/users'));
const Organizations = React.lazy(() => import('./pages/organizations'));
const Expenses = React.lazy(() => import('./pages/expenses'));
const ReportShift = React.lazy(() => import('./pages/reports.shift'));
const ReportTax = React.lazy(() => import('./pages/reports.tax'));
const ReportInvoice = React.lazy(() => import('./pages/reports.invoice'));
const Dashboard = React.lazy(() => import('./pages/dashboard'));
const NotFound = React.lazy(() => import('./pages/not-found'));
const Subscription = React.lazy(() => import('./pages/subscription/index'));
const SubscriptionSuccess = React.lazy(() => import('./pages/subscription/success'));
const ReportKm = React.lazy(() => import('./pages/reports.km'));
const ReportIncident = React.lazy(() => import('./pages/reports.incident'));

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const { Header, Content, Sider, Footer } = Layout;

const logout = () => {
  Auth.Logout();
  window.location.reload();
};

const switchToV2 = async () => {
  await ToggleFrontendVersion();
  window.location.reload();
}

const DEMO = {
  user: 'Megan',
  link: '#',
  headerLink: {
    about: '',
    help: '',
    signOut: '',
  },
};

function Logo() {
  return (
    <div className="menu-logo">
      <Link to="/">
        <img src={logoImage} alt="logo" />
        <h1>Bugal</h1>
      </Link>
    </div>
  );
}

function App() {
  const isSmallScreen = useScreenSize();
  const history = useHistory();
  const location = useLocation();

  const currentUser = useCurrentUser();
  const organizations = useCurrentOrganizations();

  const authState = useSelector((state: IState) => state.auth);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    setCollapsed(isSmallScreen);
  }, [isSmallScreen]);

  const [organizationsPendingInvite, setOrganizationsPendingInvite] = useState<Organization[]>([]);

  const hasPermissionOrganizationSettings = usePermission(Scope.OrganizationManageSettings);
  const hasPermissionOrganizationStaff = usePermission(Scope.OrganizationManageStaff);
  const hasPermissionOrganizationSubscription = usePermission(Scope.OrganizationManageSubscription);

  const handleToggle = () => {
    setCollapsed(!collapsed);
  };

  const leaveImpersonate = () => {
    Auth.LeaveImpersonate();
    history.replace('/users');
    window.location.reload();
  };

  const supportDropdown = () => {
    return (
      <Menu className="app-header-dropdown">
        <Menu.Item key="0" className="d-block">
          <a href={DEMO.link} onClick={() => window.olark('api.box.expand')}>
            Contact Us
          </a>
        </Menu.Item>
      </Menu>
    );
  };

  const avatarDropdown = () => {
    return (
      <Menu className="app-header-dropdown">
        <Menu.Item key="profile" className="d-block">
          <Link to="/profile">
            <UserOutlined />
            Profile
          </Link>
        </Menu.Item>
        {hasPermissionOrganizationSettings && (
          <Menu.Item key="seetings">
            <Link to="/business/settings">
              <SettingOutlined />
              Business settings
            </Link>
          </Menu.Item>
        )}
        {hasPermissionOrganizationStaff && (
          <Menu.Item key="staff">
            <Link to="/business/staff">
              <TeamOutlined />
              Staff
            </Link>
          </Menu.Item>
        )}
        {hasPermissionOrganizationSubscription && (
          <Menu.Item key="subscription">
            <Link to="/subscription">
              <RocketOutlined />
              Subscription
            </Link>
          </Menu.Item>
        )}
        {renderAdmin()}
        <Menu.Divider />
          <Menu.Item key="bugalv2">
            {/* <img src="/static/logo-color.png" alt="Bugal" className="h-4 w-4 mr-2 brightness-0"/> */}
            <img src={logoColor} alt="logo" height={14} className="anticon" />
            <a href={DEMO.link} onClick={(e) => { e.preventDefault(); switchToV2(); }}>
              Switch to New Bugal
            </a>
          </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="sign-out">
          <a href={DEMO.link} onClick={() => logout()}>
            <LogoutOutlined />
            Sign out
          </a>
        </Menu.Item>
      </Menu>
    );
  };

  let path = location.pathname.replace(/\//g, '');
  if (path === '') {
    path = 'dashboard';
  }

  const renderImpersonation = () => {
    if (Auth.IsImpersonated()) {
      return <span style={{ color: 'red' }}>{currentUser.fullName}</span>;
    }
  };

  const renderAdmin = () => {
    if (Auth.IsImpersonated()) {
      return (
        <Menu.Item key="4">
          <a href={DEMO.link} onClick={() => leaveImpersonate()}>
            <LogoutOutlined />
            Leave impersonate
          </a>
        </Menu.Item>
      );
    }
  };

  useEffect(() => {
    const pending = organizations.filter((i) => !!i.invitationCode);
    setOrganizationsPendingInvite(pending);
  }, [organizations]);

  if (!currentUser.firstName || !currentUser.lastName || !authState.organizations || authState.organizations.length === 0) {
    return <Setup />;
  }

  return (
    <Layout style={{ minHeight: '100vh' }} className={`${Auth.IsImpersonated() ? 'impersonating' : ''}`}>
      <Sider collapsible collapsed={collapsed} collapsedWidth={0} trigger={null} width="256" className="app-sidenav no-print">
        <Logo />
        <MenuFoldOutlined className="close-button" onClick={() => setCollapsed(true)} />
        <div style={{ flex: 1 }}>
          <AppMenu setCollapsed={setCollapsed} />
        </div>
        <div style={{ width: '100%', alignSelf: 'end' }}>
          <OrganizationDropdown />
        </div>
      </Sider>
      <Layout className={`site-layout ${collapsed ? 'menu-collapsed' : ''}`}>
        <PastDueBanner />
        <FreeTrialBanner />
        <PlanOffer />
        <Header className="site-layout-background app-header">
          <div className="app-header-inner no-print">
            <div className="header-left">
              {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                className: 'trigger',
                onClick: handleToggle,
              })}
              {renderImpersonation()}
            </div>
            {isSmallScreen && (
              <div className={`header-center ${collapsed ? 'collapsed' : ''}`}>
                <Link to="/dashboard">
                  <img src={logoColor} alt="logo" className="header-logo" />
                  <strong className="header-logo-text">Bugal</strong>
                </Link>
              </div>
            )}
            <div className="header-right">
              <div className="list-unstyled list-inline">
                <ModalContextProvider>
                  <ReferButton />
                  <ReferModal />
                </ModalContextProvider>
                <Dropdown className="list-inline-item" overlay={supportDropdown()} trigger={['click']} placement="bottomRight">
                  <a className="ant-dropdown-link no-link-style" href={DEMO.link}>
                    <QuestionCircleOutlined />
                  </a>
                </Dropdown>
                <Dropdown className="list-inline-item" overlay={avatarDropdown()} trigger={['click']} placement="bottomRight">
                  <a className="ant-dropdown-link no-link-style" href={DEMO.link}>
                    <Avatar size="small" icon={<UserOutlined />} />
                  </a>
                </Dropdown>
              </div>
            </div>
          </div>
        </Header>
        <Content>
          <Suspense fallback={<Spin indicator={antIcon} />}>
            <Switch>
              <PrivateRoute path="/business/settings" component={OrganizationSettings} scope={Scope.OrganizationManageSettings} />
              <PrivateRoute path="/business/staff" component={OrganizationStaff} scope={Scope.OrganizationManageStaff} />
              <PrivateRoute path="/profile" component={Profile} />
              <PrivateRoute path="/agreements" component={Agreements} scope={Scope.AgreementsRead} />
              <PrivateRoute path="/contacts" component={Contacts} scope={Scope.ContactsRead} />
              <PrivateRoute path="/dashboard" exact component={Dashboard} />
              <PrivateRoute path="/expenses" component={Expenses} scope={Scope.ExpensesRead} />
              <PrivateRoute path="/reports/incidents" component={ReportIncident} scope={Scope.ReportsIncident} />
              <PrivateRoute path="/reports/kms" component={ReportKm} scope={Scope.ReportsKms} />
              <PrivateRoute path="/reports/shifts" component={ReportShift} scope={Scope.ReportsShift} />
              <PrivateRoute path="/reports/tax" component={ReportTax} scope={Scope.ReportsTax} />
              <PrivateRoute path="/reports/invoices" component={ReportInvoice} scope={Scope.ReportsInvoice} />
              <PrivateRoute path="/invoices" component={Invoices} scope={Scope.InvoicesRead} />
              <PrivateRoute path="/rates" component={Rates} scope={Scope.RatesRead} />
              <PrivateRoute path="/shifts" component={Shifts} scope={Scope.ShiftsRead} />
              <PrivateRoute path="/subscription/:state" component={SubscriptionSuccess} scope={Scope.OrganizationManageSubscription} />
              <PrivateRoute path="/subscription" component={Subscription} scope={Scope.OrganizationManageSubscription} />
              <PrivateRoute path="/users" component={Users} />
              <PrivateRoute path="/organizations" component={Organizations} />
              <PrivateRoute path="/" exact component={Dashboard} />
              <PrivateRoute path="*" component={NotFound} />
            </Switch>
          </Suspense>
        </Content>
        <Footer>Copyright {new Date().getUTCFullYear()}. Bugal Software. All rights reserved.</Footer>
        <OrganizationInvitationsModal invitations={organizationsPendingInvite} />
      </Layout>
    </Layout>
  );
}

export default App;
